import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, Download, FileText, User, CreditCard, Banknote, Plus, X, RefreshCw } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const CustomerLedger = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'cash',
    reference: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const getDefaultDates = () => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    return {
      from: threeMonthsAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };

  const [filters, setFilters] = useState(getDefaultDates());

  useEffect(() => {
    fetchCustomers();
    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchLedger();
    }
    // eslint-disable-next-line
  }, [selectedCustomerId, filters]);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.filter(c => !c.is_guest));
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const syncCancelledBillsLedger = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/ledger/sync-cancelled-bills`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { entries_created, total_cancelled_bills } = response.data;
      
      if (entries_created > 0) {
        toast.success(`Ledger synced! ${entries_created} contra entries created for ${total_cancelled_bills} cancelled bills.`);
        // Refresh ledger if a customer is selected
        if (selectedCustomerId) {
          fetchLedger();
        }
      } else {
        toast.info('Ledger is already in sync. No updates needed.');
      }
    } catch (err) {
      console.error('Error syncing ledger:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync ledger');
    } finally {
      setSyncing(false);
    }
  };

  const fetchLedger = async () => {
    if (!selectedCustomerId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;

      const response = await axios.get(`${API}/customer-ledger-report/${selectedCustomerId}`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerData(response.data);
    } catch (err) {
      console.error('Error fetching ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/customer-payment`, {
        customer_id: selectedCustomerId,
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
      fetchLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.mobile?.includes(searchTerm)
  );

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const generatePDF = () => {
    if (!ledgerData || !ledgerData.customer) {
      toast.error('No data to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(businessInfo?.business_name || 'GPOS', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Customer Ledger Statement', pageWidth / 2, 22, { align: 'center' });

      // Customer Details
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Customer:', 14, 35);
      doc.setFont('helvetica', 'normal');
      doc.text(`${ledgerData.customer.name}`, 40, 35);
      
      if (ledgerData.customer.mobile) {
        doc.text(`Mobile: ${ledgerData.customer.mobile}`, 14, 42);
      }

      // Period
      doc.setFont('helvetica', 'bold');
      doc.text('Period:', 120, 35);
      doc.setFont('helvetica', 'normal');
      doc.text(`${formatDate(filters.from)} to ${formatDate(filters.to)}`, 140, 35);

      // Summary
      doc.setFillColor(245, 245, 245);
      doc.rect(14, 50, pageWidth - 28, 20, 'F');
      doc.setFontSize(10);
      doc.text(`Opening: ${formatCurrency(ledgerData.opening_balance)}`, 20, 58);
      doc.text(`Credits: ${formatCurrency(ledgerData.period_credits)}`, 60, 58);
      doc.text(`Payments: ${formatCurrency(ledgerData.period_payments)}`, 100, 58);
      doc.setFont('helvetica', 'bold');
      doc.text(`Closing: ${formatCurrency(ledgerData.closing_balance)}`, 150, 58);
      doc.setFont('helvetica', 'normal');
      doc.text(`All-time Balance: ${formatCurrency(ledgerData.all_time_balance)}`, 20, 66);

      // Table
      const tableData = [];
      
      // Opening balance row
      tableData.push([formatDate(filters.from), 'Opening Balance', '-', '-', '-', formatCurrency(ledgerData.opening_balance)]);
      
      // Transactions
      ledgerData.transactions.forEach(txn => {
        tableData.push([
          formatDateTime(txn.created_at),
          txn.type === 'credit' ? 'Credit Sale' : 'Payment',
          txn.reference_number || txn.reference || '-',
          txn.type === 'credit' ? formatCurrency(txn.amount) : '-',
          txn.type === 'payment' ? formatCurrency(txn.amount) : '-',
          formatCurrency(txn.running_balance)
        ]);
      });

      autoTable(doc, {
        startY: 75,
        head: [['Date', 'Type', 'Reference', 'Debit', 'Credit', 'Balance']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [124, 58, 237], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 30 },
          2: { cellWidth: 28 },
          3: { cellWidth: 28, halign: 'right' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 28, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });

      // Footer
      const finalY = (doc.lastAutoTable?.finalY || 85) + 10;
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, finalY);

      // Save
      const fileName = `${ledgerData.customer.name.replace(/\s+/g, '_')}_Ledger_${filters.from}_to_${filters.to}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div data-testid="customer-ledger-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Customer Ledger</h1>
            <p className="text-slate-500">View transactions & record payments</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Sync Ledger button - always visible for owner */}
          <button
            onClick={syncCancelledBillsLedger}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50"
            data-testid="sync-ledger-btn"
            title="Sync ledger with cancelled bills"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync Ledger'}
          </button>
          {ledgerData && (
            <>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700"
                data-testid="record-payment-btn"
              >
                <Plus size={18} />
                Record Payment
              </button>
              <button
                onClick={generatePDF}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
                data-testid="download-pdf-btn"
              >
                <Download size={18} />
                Download PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Customer Selection & Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Customer</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or mobile..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            {searchTerm && filteredCustomers.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                {filteredCustomers.slice(0, 10).map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomerId(customer.id);
                      setSearchTerm(customer.name);
                    }}
                    className={`p-3 cursor-pointer hover:bg-violet-50 ${selectedCustomerId === customer.id ? 'bg-violet-100' : ''}`}
                  >
                    <p className="font-medium">{customer.name}</p>
                    <p className="text-sm text-slate-500">{customer.mobile}</p>
                  </div>
                ))}
              </div>
            )}
            {!searchTerm && (
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="mt-2 w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">-- Select Customer --</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.mobile})</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date Range</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <button
              onClick={() => setFilters(getDefaultDates())}
              className="mt-2 text-sm text-violet-600 hover:underline"
            >
              Reset to Last 3 Months
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-violet-200 border-t-violet-600"></div>
        </div>
      )}

      {/* Ledger Data */}
      {!loading && ledgerData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Opening Balance</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(ledgerData.opening_balance)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Credit Sales</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(ledgerData.period_credits)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Payments Received</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(ledgerData.period_payments)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Closing Balance</p>
              <p className="text-xl font-bold text-violet-600">{formatCurrency(ledgerData.closing_balance)}</p>
            </div>
            <div className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl p-4 text-white">
              <p className="text-sm opacity-80">All-Time Balance</p>
              <p className="text-xl font-bold">{formatCurrency(ledgerData.all_time_balance)}</p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                <User className="text-violet-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{ledgerData.customer.name}</h3>
                <p className="text-slate-500">
                  {ledgerData.customer.mobile}
                  {ledgerData.customer.pricing_level && ` • Level ${ledgerData.customer.pricing_level}`}
                </p>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold">Transactions ({ledgerData.transaction_count})</h3>
              <span className="text-sm text-slate-500">{formatDate(filters.from)} - {formatDate(filters.to)}</span>
            </div>
            
            {ledgerData.transactions.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="mx-auto mb-4 text-slate-300" size={48} />
                <p className="text-slate-500">No transactions found for this period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Reference</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Debit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Credit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr className="bg-slate-50">
                      <td className="px-4 py-3 text-sm">{formatDate(filters.from)}</td>
                      <td className="px-4 py-3 text-sm font-medium">Opening Balance</td>
                      <td className="px-4 py-3 text-sm text-slate-500">-</td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(ledgerData.opening_balance)}</td>
                    </tr>
                    {ledgerData.transactions.map((txn, idx) => (
                      <tr key={txn.id || idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{formatDateTime(txn.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            txn.type === 'credit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {txn.type === 'credit' ? <CreditCard size={12} /> : <Banknote size={12} />}
                            {txn.type === 'credit' ? 'Credit Sale' : 'Payment'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {txn.reference_number || txn.reference || '-'}
                          {txn.notes && <span className="block text-xs text-slate-400">{txn.notes}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                          {txn.type === 'credit' ? formatCurrency(txn.amount) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                          {txn.type === 'payment' ? formatCurrency(txn.amount) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">{formatCurrency(txn.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-violet-50">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 font-semibold">Closing Balance</td>
                      <td className="px-4 py-3 text-right font-semibold text-red-600">{formatCurrency(ledgerData.period_credits)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">{formatCurrency(ledgerData.period_payments)}</td>
                      <td className="px-4 py-3 text-right font-bold text-violet-600">{formatCurrency(ledgerData.closing_balance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && !ledgerData && !selectedCustomerId && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <User className="mx-auto mb-4 text-slate-300" size={48} />
          <p className="text-slate-500">Select a customer to view their ledger</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Record Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
                <p className="px-4 py-2 bg-slate-50 rounded-lg">{ledgerData?.customer?.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current Balance</label>
                <p className={`px-4 py-2 rounded-lg font-semibold ${ledgerData?.all_time_balance > 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                  {formatCurrency(ledgerData?.all_time_balance)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select
                  value={paymentForm.payment_mode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reference # (Optional)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="Transaction ID, Cheque No, etc."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Any additional notes..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-2 border border-slate-200 rounded-lg font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={submitting || !paymentForm.amount}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerLedger;
